import { useState, useCallback, useEffect, useRef } from 'react';
import { getRecommendedBots, BotType } from '@/Data/Bot';
import FriendCard from '@/Components/FriendCard';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChatIcon from '@mui/icons-material/Chat';
import SearchIcon from '@mui/icons-material/Search';
import './chat.scss';
import { tLoader } from '@/Utils/i18n';
import { languageManager } from '@/Utils/languageManager';

const translations = {
    en: {
        "Find My Korean Mate": "Find My Korean Mate",
        "Discover your perfect Korean study partner!": "Discover your perfect Korean study partner!",
        "Practice conversations, learn new expressions,": "Practice conversations, learn new expressions,",
        "and immerse yourself in Korean culture": "and immerse yourself in Korean culture",
        "with AI friends who match your interests.": "with AI friends who match your interests.",
        "Find me mates!": "Find me mates!",
        "Your Mate": "Your Mate",
        "Select your study Mate.": "Select your study Mate.",
        "Let's chat now": "Let's chat now",
        "I will choose again": "I will choose again",
        "Meeting Korean and Indonesian friends, becoming best friends...": "Meeting Korean and English friends, becoming best friends...",
        "Visiting Korea and Indonesia many times to discover new cultures...": "Visiting Korea and the World many times to discover new cultures...",
        "Looking for a MATE ready to accompany you...": "Looking for a MATE ready to accompany you...",
        "Preparing conversations to learn Korean naturally...": "Preparing conversations to learn Korean naturally...",
        "Connecting cultures and creating meaningful connections...": "Connecting cultures and creating meaningful connections..."
    },
    id: {
        "Find My Korean Mate": "Temukan Mate Korea Saya",
        "Discover your perfect Korean study partner!": "Temukan teman belajar bahasa Korea yang sempurna!",
        "Practice conversations, learn new expressions,": "Latih percakapan, pelajari ekspresi baru,",
        "and immerse yourself in Korean culture": "dan benamkan diri dalam budaya Korea",
        "with AI friends who match your interests.": "dengan teman AI yang sesuai dengan minatmu.",
        "Find me mates!": "Carikan mate!",
        "Your Mate": "Mate Kamu",
        "Select your study Mate.": "Pilih Mate belajarmu.",
        "Let's chat now": "Ayo ngobrol sekarang",
        "I will choose again": "Saya akan pilih lagi",
        "Meeting Korean and Indonesian friends, becoming best friends...": "Bertemu teman Korea dan Indonesia, menjadi sahabat terbaik...",
        "Visiting Korea and Indonesia many times to discover new cultures...": "Mengunjungi Korea dan Indonesia berkali-kali untuk menemukan budaya baru...",
        "Looking for a MATE ready to accompany you...": "Mencari MATE yang siap menemanimu...",
        "Preparing conversations to learn Korean naturally...": "Menyiapkan percakapan untuk belajar bahasa Korea secara alami...",
        "Connecting cultures and creating meaningful connections...": "Menghubungkan budaya dan menciptakan koneksi bermakna..."
    },
    ko: {
        "Find My Korean Mate": "나의 한국어 메이트 찾기",
        "Discover your perfect Korean study partner!": "완벽한 한국어 학습 파트너를 찾아보세요!",
        "Practice conversations, learn new expressions,": "대화를 연습하고, 새로운 표현을 배우고,",
        "and immerse yourself in Korean culture": "한국 문화에 빠져보세요",
        "with AI friends who match your interests.": "당신의 관심사에 맞는 AI 친구와 함께.",
        "Find me mates!": "메이트 찾기!",
        "Your Mate": "당신의 메이트",
        "Select your study Mate.": "학습 메이트를 선택하세요.",
        "Let's chat now": "지금 채팅하기",
        "I will choose again": "다시 선택할게요",
        "Meeting Korean and Indonesian friends, becoming best friends...": "한국과 인도네시아 친구들을 만나 최고의 친구가 되는 중...",
        "Visiting Korea and Indonesia many times to discover new cultures...": "새로운 문화를 발견하기 위해 한국과 인도네시아를 여러 번 방문하는 중...",
        "Looking for a MATE ready to accompany you...": "당신과 함께할 MATE를 찾는 중...",
        "Preparing conversations to learn Korean naturally...": "자연스럽게 한국어를 배울 대화를 준비하는 중...",
        "Connecting cultures and creating meaningful connections...": "문화를 연결하고 의미 있는 관계를 만드는 중..."
    },
    vn: {
        "Find My Korean Mate": "Tìm Bạn Học Tiếng Hàn",
        "Discover your perfect Korean study partner!": "Khám phá đối tác học tiếng Hàn hoàn hảo của bạn!",
        "Practice conversations, learn new expressions,": "Thực hành hội thoại, học các biểu thức mới,",
        "and immerse yourself in Korean culture": "và đắm mình trong văn hóa Hàn Quốc",
        "with AI friends who match your interests.": "với bạn AI phù hợp với sở thích của bạn.",
        "Find me mates!": "Tìm bạn cho tôi!",
        "Your Mate": "Bạn Của Bạn",
        "Select your study Mate.": "Chọn bạn học của bạn.",
        "Let's chat now": "Trò chuyện ngay",
        "I will choose again": "Tôi sẽ chọn lại",
        "Meeting Korean and Indonesian friends, becoming best friends...": "Gặp gỡ bạn bè Hàn Quốc và Viết bài về, trở thành bạn thân nhất...",
        "Visiting Korea and Indonesia many times to discover new cultures...": "Đến thăm Hàn Quốc và Viết Nam nhiều lần để khám phá văn hóa mới...",
        "Looking for a MATE ready to accompany you...": "Đang tìm MATE sẵn sàng đồng hành cùng bạn...",
        "Preparing conversations to learn Korean naturally...": "Chuẩn bị các cuộc trò chuyện để học tiếng Hàn tự nhiên...",
        "Connecting cultures and creating meaningful connections...": "Kết nối văn hóa và tạo ra những kết nối có ý nghĩa..."
    }
};

const APP_STORE_LINK = 'https://apps.apple.com/app/make-korean-friend/id6499510702';
const PLAY_STORE_LINK = 'https://play.google.com/store/apps/details?id=com.sugeul.makekoreanfriend';

const getLoadingMessages = (t: (key: string) => string) => [
    { emoji: '🤝', text: t('Meeting Korean and Indonesian friends, becoming best friends...') },
    { emoji: '✈️', text: t('Visiting Korea and Indonesia many times to discover new cultures...') },
    { emoji: '🔍', text: t('Looking for a MATE ready to accompany you...') },
    { emoji: '💬', text: t('Preparing conversations to learn Korean naturally...') },
    { emoji: '🌏', text: t('Connecting cultures and creating meaningful connections...') },
];

// Calculate typing duration: 100ms initial + 50ms per char + 450ms extra per space + 1000ms end pause
const calculateTypingDuration = (text: string) => {
    const spaces = (text.match(/ /g) || []).length;
    return 100 + (text.length * 50) + (spaces * 450) + 1000;
};

// Typing effect component
const TypingText = ({ text, onComplete }: { text: string; onComplete?: () => void }) => {
    const [displayedText, setDisplayedText] = useState('');
    const onCompleteRef = useRef(onComplete);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Keep onComplete ref updated without triggering effect
    useEffect(() => {
        onCompleteRef.current = onComplete;
    }, [onComplete]);

    useEffect(() => {
        setDisplayedText('');
        let index = 0;

        // Clear any existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        const typeNextChar = () => {
            if (index < text.length) {
                const char = text[index];
                index++;
                setDisplayedText(text.slice(0, index));
                // Pause 500ms at space, otherwise 50ms
                const delay = char === ' ' ? 500 : 50;
                timeoutRef.current = setTimeout(typeNextChar, delay);
            } else {
                onCompleteRef.current?.();
            }
        };

        timeoutRef.current = setTimeout(typeNextChar, 100);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [text]); // Only depend on text, not onComplete

    return <>{displayedText}<span className="Cursor">|</span></>;
};

const ChatPage = () => {
    const [, forceUpdate] = useState({});
    const t = tLoader(translations);

    useEffect(() => {
        const unsubscribe = languageManager.subscribe(() => forceUpdate({}));
        return unsubscribe;
    }, []);

    const LOADING_MESSAGES = getLoadingMessages(t);
    const TOTAL_LOADING_DURATION = LOADING_MESSAGES.reduce(
        (total, msg) => total + calculateTypingDuration(msg.text),
        0
    );

    const [bots, setBots] = useState<BotType[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
    const [typingComplete, setTypingComplete] = useState(false);
    const [showCarousel, setShowCarousel] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedBot, setSelectedBot] = useState<BotType | null>(null);

    // Move to next message when typing is complete
    useEffect(() => {
        if (!loading || !typingComplete) return;
        const timeout = setTimeout(() => {
            setLoadingMessageIndex(prev => (prev + 1) % LOADING_MESSAGES.length);
            setTypingComplete(false);
        }, 1000); // Wait 1 second at end of sentence before next message
        return () => clearTimeout(timeout);
    }, [loading, typingComplete, LOADING_MESSAGES.length]);

    const handleFindMates = async () => {
        setLoading(true);
        setLoadingMessageIndex(0);
        setTypingComplete(false);
        // Fetch bots (loading continues until all messages shown)
        const recommended = await getRecommendedBots(3);
        setBots(recommended);
        // Start with middle card selected (index 1 for 3 cards)
        if (recommended.length >= 3) {
            setCurrentIndex(1);
        }
        // Wait for all messages to complete (calculated from actual timing)
        await new Promise(resolve => setTimeout(resolve, TOTAL_LOADING_DURATION));
        setLoading(false);
        setShowCarousel(true);
    };

    const handlePrev = useCallback(() => {
        setCurrentIndex(prev => (prev > 0 ? prev - 1 : bots.length - 1));
        setSelectedBot(null);
    }, [bots.length]);

    const handleNext = useCallback(() => {
        setCurrentIndex(prev => (prev < bots.length - 1 ? prev + 1 : 0));
        setSelectedBot(null);
    }, [bots.length]);

    const handleCardClick = (bot: BotType, index: number) => {
        if (selectedBot?.botid === bot.botid) {
            setSelectedBot(null);
        } else {
            // Move clicked card to center and select it
            setCurrentIndex(index);
            setSelectedBot(bot);
        }
    };

    const handleChatNow = () => {
        // Detect platform and redirect to app store
        const userAgent = navigator.userAgent.toLowerCase();
        if (/iphone|ipad|ipod/.test(userAgent)) {
            window.location.href = APP_STORE_LINK;
        } else {
            window.location.href = PLAY_STORE_LINK;
        }
    };

    const handleChooseAgain = () => {
        setSelectedBot(null);
    };

    if (loading) {
        const currentMessage = LOADING_MESSAGES[loadingMessageIndex];
        return (
            <div className="ChatPage">
                <div className="GradientBg" />
                <div className="LoadingSection">
                    <span className="Emoji" key={`emoji-${loadingMessageIndex}`}>{currentMessage.emoji}</span>
                    <p className="LoadingMessage" key={`msg-${loadingMessageIndex}`}>
                        <TypingText
                            text={currentMessage.text}
                            onComplete={() => setTypingComplete(true)}
                        />
                    </p>
                    <div className="LoadingDots">
                        <span /><span /><span />
                    </div>
                </div>
            </div>
        );
    }

    // Intro screen - before finding mates
    if (!showCarousel) {
        return (
            <div className="ChatPage">
                <div className="GradientBg" />
                <div className="IntroSection">
                    <span className="Emoji">🇰🇷</span>
                    <h1>{t("Find My Korean Mate")}</h1>
                    <p>
                        {t("Discover your perfect Korean study partner!")}<br />
                        {t("Practice conversations, learn new expressions,")}<br />
                        {t("and immerse yourself in Korean culture")}<br />
                        {t("with AI friends who match your interests.")}
                    </p>
                    <button className="FindButton" onClick={handleFindMates}>
                        <SearchIcon /> {t("Find me mates!")}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="ChatPage">
            {/* Gradient background */}
            <div className="GradientBg" />

            {/* Header */}
            <div className="Header">
                <span className="Emoji">🚀</span>
                <h1>{t("Your Mate")}</h1>
                <p>{t("Select your study Mate.")}</p>
            </div>

            {/* Carousel */}
            <div className="Carousel">
                {/* Navigation arrows */}
                {bots.length > 1 && !selectedBot && (
                    <>
                        <button className="NavButton Prev" onClick={handlePrev}>
                            <ChevronLeftIcon />
                        </button>
                        <button className="NavButton Next" onClick={handleNext}>
                            <ChevronRightIcon />
                        </button>
                    </>
                )}

                {/* Cards */}
                <div className="CardsContainer">
                    {bots.map((bot, index) => {
                        const offset = index - currentIndex;
                        const isVisible = !selectedBot || selectedBot.botid === bot.botid;
                        const isSelected = selectedBot?.botid === bot.botid;

                        return (
                            <div
                                key={bot.botid}
                                className={`CardWrapper ${isVisible ? '' : 'Hidden'}`}
                                style={{
                                    transform: `
                                        translateX(${offset * 80}%)
                                        rotate(${offset * 15}deg)
                                        scale(${1 - Math.abs(offset) * 0.2})
                                        translateY(${Math.abs(offset) * 20}px)
                                    `,
                                    zIndex: 10 - Math.abs(offset),
                                    opacity: isVisible ? 1 : 0,
                                }}
                            >
                                <FriendCard
                                    friend={bot}
                                    onClick={() => handleCardClick(bot, index)}
                                    selected={isSelected}
                                    staticMode
                                />
                            </div>
                        );
                    })}
                </div>

                {/* Action buttons - shown below carousel when bot selected */}
                <div className={`ActionGroup ${selectedBot ? 'Visible' : ''}`}>
                    <button className="ChatButton" onClick={handleChatNow}>
                        <ChatIcon /> {t("Let's chat now")}
                    </button>
                    <button className="ChooseAgainButton" onClick={handleChooseAgain}>
                        {t("I will choose again")}
                    </button>
                </div>
            </div>

            {/* Dots indicator */}
            {!selectedBot && bots.length > 1 && (
                <div className="DotsIndicator">
                    {bots.map((_, index) => (
                        <span
                            key={index}
                            className={`Dot ${index === currentIndex ? 'Active' : ''}`}
                            onClick={() => setCurrentIndex(index)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ChatPage;
